import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, FileText, IdCard, Mail, User } from 'lucide-react';
import { Button } from '@bliks-io/ui';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { downloadUsersTemplate, type UserInput } from '@/lib/excel';
import type { PersonioSetupUnsuppliedPerson } from '@bliks-io/contracts';
import { unsuppliedPerson } from '@/pages/organization-settings/data-sync/syncSelection';
import { UnsuppliedPeopleNote } from '@/pages/organization-settings/data-sync/UnsuppliedPeopleNote';
import { useAnchorSeedRows } from '../useAnchorSeedRows';
import { usePersonioImport } from '../usePersonioImport';
import { useSeedOnOpen, type PersonioSeed } from '../useSeedOnOpen';
import { isClearedAnchorDraft } from '../utils/flushBaselines';
import { parseUsersFromExcel, type ParsedUser } from '../utils/excelParsers';
import { departmentOptions, resolveDepartmentToId } from '@/lib/department-ref';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import { userDuplicateKeys, validateUserRow } from '../utils/draftRowValidation';
import { upsertUploadedRows } from '@/lib/draft-rows/upsertUploadedRows';
import { mergeSetupUsers } from '../utils/mergeSetupUsers';
import {
  effectiveAnchorRows,
  isProtectedRow,
  mergeUploadedUserRow,
  protectedUserIds
} from '../utils/protectedRows';
import { EditableDraftTable, type DraftColumnDef } from '../components/EditableDraftTable';
import { type DraftRowShape } from '@/lib/draft-rows/draftRow';
import { DraftTableStep } from '../components/DraftTableStep';
import { explainerFieldsFromColumns, explainerIcon } from '../components/FieldExplainer';

interface UsersStepProps {
  users: ParsedUser[];
  onUsersChange: (users: ParsedUser[]) => void;
  submitError?: string;
  /**
   * Step already imported — table is seeded from the import; on Weiter new
   * rows append, edits update in place and removals delete surgically
   * (ADR 0011, third amendment)
   */
  isDone?: boolean;
  /** The step's flush baseline — an empty draft with a surviving baseline is a cleared table */
  flushBaseline?: ParsedUser[];
  /** Personio rows that fill the table when the step opens without new rows */
  personioSeed?: PersonioSeed<ParsedUser[]>;
  /** People Personio's read or export upload cannot fill a row with, listed while not in the table */
  unsupplied?: PersonioSetupUnsuppliedPerson[];
  /** Receives the people each upload cannot fill a row with */
  onUploadUnsupplied: (people: PersonioSetupUnsuppliedPerson[]) => void;
}

const makeEmptyUserRow = (rowNumber: number): DraftRowShape<UserInput> => ({
  data: { firstName: '', lastName: '', email: '', department: '', role: '' },
  isValid: true,
  rowNumber
});

const USER_COLUMNS: DraftColumnDef<UserInput>[] = [
  {
    field: 'firstName',
    headerKey: 'bulkOnboarding.excel.columns.users.firstName',
    descriptionKey: 'initialSetup.columnHints.users.firstName',
    required: true,
    explainer: {
      icon: explainerIcon(User),
      descriptionKey: 'initialSetup.fieldExplainer.fields.users.firstName'
    }
  },
  {
    field: 'lastName',
    headerKey: 'bulkOnboarding.excel.columns.users.lastName',
    descriptionKey: 'initialSetup.columnHints.users.lastName',
    required: true,
    explainer: {
      icon: explainerIcon(User),
      descriptionKey: 'initialSetup.fieldExplainer.fields.users.lastName'
    }
  },
  {
    field: 'email',
    headerKey: 'bulkOnboarding.excel.columns.users.email',
    descriptionKey: 'initialSetup.columnHints.users.email',
    required: true,
    explainer: {
      icon: explainerIcon(Mail),
      descriptionKey: 'initialSetup.fieldExplainer.fields.users.email'
    }
  },
  {
    field: 'department',
    headerKey: 'bulkOnboarding.excel.columns.users.department',
    descriptionKey: 'initialSetup.columnHints.users.department',
    required: true,
    explainer: {
      icon: explainerIcon(Building2),
      descriptionKey: 'initialSetup.fieldExplainer.fields.users.department'
    }
  },
  {
    field: 'role',
    headerKey: 'bulkOnboarding.excel.columns.users.role',
    descriptionKey: 'initialSetup.columnHints.users.role',
    required: true,
    explainer: {
      icon: explainerIcon(IdCard),
      descriptionKey: 'initialSetup.fieldExplainer.fields.users.role'
    }
  },
  {
    field: 'roleDescription',
    headerKey: 'bulkOnboarding.excel.columns.users.roleDescription',
    descriptionKey: 'initialSetup.columnHints.users.roleDescription',
    optional: true,
    // One Stelle owns one shared Beschreibung (a single OrgFunction on the
    // backend) — editing it on any row updates every row with that Stelle
    // immediately, instead of silently collapsing to one value after Weiter.
    syncRowsAfterEdit: (rows, editedIndex) => {
      const edited = rows[editedIndex];
      const role = normalizedKey(edited?.role);
      if (!role) return rows;
      return rows.map((row, index) =>
        index !== editedIndex && normalizedKey(row.role) === role
          ? { ...row, roleDescription: edited?.roleDescription }
          : row
      );
    },
    explainer: {
      icon: explainerIcon(FileText),
      descriptionKey: 'initialSetup.fieldExplainer.fields.users.roleDescription'
    }
  }
];

export function UsersStep({
  users,
  onUsersChange,
  submitError,
  isDone = false,
  flushBaseline,
  personioSeed,
  unsupplied = [],
  onUploadUnsupplied
}: UsersStepProps) {
  const { t } = useTranslation();
  const { data: existingDepartments, isSuccess: departmentsLoaded } = useDepartments();
  // Externals never take part in the Ersteinrichtung (ADR 0011) — the org
  // structure is built from internal members only.
  const { data: orgMembers, isSuccess: membersLoaded } = useOrgMembers({
    status: ['inactive', 'pending', 'active'],
    classification: 'internal'
  });

  const { userSeeds } = useAnchorSeedRows();

  // Options carry the stable id as value so a later Abkürzung rename auto-follows;
  // the list includes rows whose manager is still unresolved.
  const departmentSelectOptions = useMemo(
    () => departmentOptions(existingDepartments ?? []),
    [existingDepartments]
  );

  // Members the add-users pipeline may never touch: their rows render with
  // locked identity columns and no delete action; department/role edits
  // commit via the placement PATCH (utils/protectedRows).
  const protectedIds = useMemo(
    () => protectedUserIds(orgMembers ?? [], isDone),
    [orgMembers, isDone]
  );

  const isProtected = (row: DraftRowShape<UserInput>) => isProtectedRow(row, protectedIds);

  const columns: DraftColumnDef<UserInput>[] = USER_COLUMNS.map((column) => {
    if (column.field === 'department') return { ...column, options: departmentSelectOptions };
    if (column.field === 'firstName' || column.field === 'lastName' || column.field === 'email') {
      return {
        ...column,
        readOnly: isProtected,
        readOnlyHintKey: 'initialSetup.protectedRows.lockedIdentity'
      };
    }
    return column;
  });

  // Protected rows are always on screen — also on a fresh step whose draft is
  // still empty, so the org's precreated admin is placeable before the first
  // import ever runs. The SAME rule feeds the Weiter submit and the
  // Weiter-enable gate: what is displayed is what submits.
  const rows = effectiveAnchorRows({
    draftRows: users,
    seedRows: userSeeds,
    protectedIds,
    isDone,
    baseline: flushBaseline
  });

  // Until the first NEW user exists, the step keeps the upload empty state
  // (dropzone + add manually) and shows the existing members in their own
  // editable table below it — they merge into the main table as soon as new
  // rows appear. Submit and gating are unaffected: both recompute the full
  // effective rows themselves.
  const protectedDisplayRows = rows.filter(isProtected);
  const hasNewUserRows = rows.length > protectedDisplayRows.length;

  const resolveDepartments = (imported: ParsedUser[]) =>
    imported.map((user) => ({
      ...user,
      data: {
        ...user.data,
        department: resolveDepartmentToId(user.data.department, existingDepartments ?? [])
      }
    }));
  // Resolve the sheet's department abbreviation to the stable id at the
  // import boundary; a repeat import updates matching on-screen rows (drafts
  // or seeds) in place instead of stacking duplicates. A row matching a
  // protected member by email fills that row's placement fields — its
  // locked identity stays as the server knows it. An empty import must not
  // touch state — it would materialize the seeds into drafts and dirty the
  // wizard without changing anything.
  const mergeImportedUsers = (imported: ParsedUser[]) => {
    const resolved = resolveDepartments(imported);
    if (resolved.length > 0) {
      onUsersChange(
        upsertUploadedRows(rows, resolved, userDuplicateKeys, mergeUploadedUserRow(protectedIds))
      );
    }
  };

  const mergePersonioUsers = (imported: ParsedUser[]) => {
    const merged = mergeSetupUsers(rows, resolveDepartments(imported));
    if (merged !== rows) onUsersChange(merged);
  };

  const handleParse = async (file: File) => {
    // Without the member list the protected-row matching would silently
    // no-op and protected members would duplicate — refuse instead.
    if (!membersLoaded) {
      toast.error(t('initialSetup.usersImport.membersNotLoaded'));
      throw new Error('org members not loaded yet');
    }
    const result = await parseUsersFromExcel(file);
    // Personio's employee export merges like a Personio read; a template upload keeps its list.
    if (result.personioExport) {
      onUploadUnsupplied(result.unsupplied);
      mergePersonioUsers(result.users);
    } else mergeImportedUsers(result.users);
    if (result.users.length > 0) personioSeed?.onReplaced();
    return result.users.length;
  };

  // Derived, not stored: deleting an added row lists the person again.
  const tableEmails = new Set(rows.map((row) => normalizedKey(row.data.email)));
  const notInTable = unsupplied.flatMap((person, index) =>
    person.email && tableEmails.has(normalizedKey(person.email))
      ? []
      : [{ ...unsuppliedPerson(`unsupplied-${index}`, person, person.reasons), person }]
  );
  const addUnsupplied = ({ firstName, lastName, email }: PersonioSetupUnsuppliedPerson) => {
    const data: UserInput = { firstName, lastName, email: email ?? '', department: '', role: '' };
    onUsersChange([...rows, { data, ...validateUserRow(data), rowNumber: rows.length + 1 }]);
  };
  const unsuppliedNote = (
    <UnsuppliedPeopleNote
      people={notInTable}
      source="personio"
      action={({ person }) =>
        // Without an email a row could not take the person off the note.
        person.email && (
          <Button
            variant="link"
            type="button"
            size="small"
            className="h-auto"
            onClick={() => addUnsupplied(person)}
          >
            {t('common.add')}
          </Button>
        )
      }
    />
  );

  // A table without new rows no longer shows Personio rows, whatever comes next.
  const handleRowsChange = (next: ParsedUser[]) => {
    if (!next.some((row) => !isProtected(row))) personioSeed?.onReplaced();
    onUsersChange(next);
  };

  const personioImport = usePersonioImport({ onLoaded: personioSeed?.onLoaded });

  // Seeds only into a table the admin sees, so autosave only ever saves rows
  // shown here. On open only an empty table of an open step takes the rows;
  // the step's own load merges into any table.
  useSeedOnOpen({
    personioSeed,
    ready: membersLoaded && departmentsLoaded,
    seed: (imported) => {
      if (imported.length === 0) return false;
      if (!personioSeed?.ownLoad && (isDone || hasNewUserRows)) return false;
      mergePersonioUsers(imported);
      return true;
    }
  });

  return (
    <DraftTableStep
      title={t('bulkOnboarding.users.title')}
      description={t(
        personioSeed?.loaded && hasNewUserRows
          ? 'initialSetup.personio.loaded'
          : 'bulkOnboarding.users.description'
      )}
      isDone={isDone}
      doneNoticeKeys={{
        titleKey: 'initialSetup.doneNotice.users.title',
        descriptionKey: 'initialSetup.doneNotice.users.description'
      }}
      rows={rows}
      draftRows={users}
      onRowsChange={handleRowsChange}
      clearScope={isDone ? 'all' : 'drafts'}
      // "Liste leeren" empties the draft; protected rows stay on screen
      // through effectiveAnchorRows (resolved fresh, flush baseline first) —
      // the wizard can never delete a protected member, and clear never
      // materializes a stale seed snapshot the next flush would PATCH back.
      deleteAction={(row) => (isProtected(row) ? 'hidden' : 'enabled')}
      pendingDeleteAll={isClearedAnchorDraft(users, flushBaseline, protectedIds)}
      columns={columns}
      validateRow={validateUserRow}
      duplicateChecks={userDuplicateKeys}
      makeEmptyRow={makeEmptyUserRow}
      addLabelKey="initialSetup.addRow.employee"
      titleKey="initialSetup.tables.users"
      uploadTitle={t('bulkOnboarding.users.uploadTitle')}
      explainerHeadingKey="initialSetup.fieldExplainer.headings.users"
      explainerSections={[{ fields: explainerFieldsFromColumns(columns) }]}
      downloadTemplate={downloadUsersTemplate}
      parse={handleParse}
      importActions={personioImport.toolbarButton}
      submitError={submitError}
      footer={unsuppliedNote}
      isEmpty={!hasNewUserRows}
      emptyExtras={
        <>
          {protectedDisplayRows.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-foreground">
                {t('initialSetup.existingEntities.title', { count: protectedDisplayRows.length })}
              </h3>
              <p className="mb-2 text-xs text-muted-foreground">
                {t('initialSetup.existingEntities.editableNote')}
              </p>
              <EditableDraftTable
                rows={protectedDisplayRows}
                // In this branch the effective row set IS the protected set, so
                // the table's rows pass straight into the draft; edits commit
                // via the placement PATCH on Weiter/autosave as usual.
                onRowsChange={onUsersChange}
                columns={columns}
                validateRow={validateUserRow}
                duplicateChecks={userDuplicateKeys}
                titleKey="initialSetup.tables.users"
                deleteAction={() => 'hidden'}
              />
            </div>
          )}
          {unsuppliedNote}
        </>
      }
    />
  );
}
